import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TeamInfo } from '../TeamInfo';
import { PlannedStint, StrategyActuals, StrategyPlan, StrategySummary } from '../types';
import {
  StrategyConflictError,
  fetchStrategy,
  listStrategies,
  listStrategyPlans,
  patchStrategyActuals,
  resetStrategyActuals,
  saveStrategy,
} from '../auth/api';
import {
  LivePlan,
  actualsUpTo,
  diffActuals,
  emptyObservations,
  matchStrategyToTeam,
  mergeActuals,
  observeTeam,
  observedActuals,
  raceClock,
  reconcile,
  stopsMade,
} from '../analytics/liveStrategy';

/** How soon a failed write is tried again, so an outage does not become a storm. */
const RETRY_AFTER_MS = 5_000;

/** How often the clock-driven numbers refresh when no frame arrives to do it. */
const TICK_MS = 1_000;

export interface LiveTeamPlan {
  /** The saved plan with any unsaved edits applied. */
  strategy: StrategyPlan;
  teamId: string;
  /** The race laid over it. */
  live: LivePlan;
  /** True while there are edits that have not been saved. */
  dirty: boolean;
}

export interface UseLiveStrategyOptions {
  teams: Map<string, TeamInfo>;
  /** The race whose strategies to follow; null when nothing is being recorded. */
  eventId: string | null;
  sessionEndsAt: number | null;
  /** Bumped by the socket whenever any strategy changes anywhere. */
  strategyVersion?: number;
  /**
   * A replay's virtual clock. Supplying it makes everything read-only: nothing is
   * written, and only the part of the finished race's record that is already in
   * the past at the playhead is used.
   */
  now?: () => number;
}

export interface UseLiveStrategy {
  plans: LiveTeamPlan[];
  /** Plans for this race whose team is not (yet) on the grid. */
  waiting: StrategyPlan[];
  /** The reconciled plan per team, for the alert pipeline. */
  byTeam: Map<string, LivePlan>;
  loading: boolean;
  error: string | null;
  readOnly: boolean;
  savingId: string | null;
  dismissError: () => void;
  edit: (strategyId: string, stintIndex: number, patch: Partial<PlannedStint>) => void;
  discard: (strategyId: string) => void;
  /** Save the reconciled plan as the new baseline (edits included, if any). */
  commit: (strategyId: string) => Promise<void>;
  /** Point a plan at a team in this race. */
  attach: (strategyId: string, teamId: string) => Promise<void>;
  candidates: StrategySummary[];
  loadCandidates: () => Promise<void>;
  reload: () => void;
}

type Drafts = Record<string, Record<number, Partial<PlannedStint>>>;

function message(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function withDrafts(strategy: StrategyPlan, draft: Drafts[string] | undefined): StrategyPlan {
  if (!draft) return strategy;
  return {
    ...strategy,
    stints: strategy.stints.map((s, i) => (draft[i] ? { ...s, ...draft[i] } : s)),
  };
}

/**
 * Follow the strategies attached to the race being recorded.
 *
 * Each plan is matched to its team on the grid, watched for the stops that really
 * happened, and laid over the plan (`reconcile`). Whatever this browser sees is
 * sent to the server as a merge — never a replacement — so any number of screens
 * can watch the same race and agree. The plan the person typed is never touched
 * automatically; only an explicit save rewrites it.
 */
export function useLiveStrategy({
  teams,
  eventId,
  sessionEndsAt,
  strategyVersion = 0,
  now,
}: UseLiveStrategyOptions): UseLiveStrategy {
  const replay = now !== undefined;

  const [strategies, setStrategies] = useState<StrategyPlan[]>([]);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<StrategySummary[]>([]);
  const [reloads, setReloads] = useState(0);
  const [tick, setTick] = useState(0);

  const storeRef = useRef(emptyObservations());
  /** What this browser has already sent, so a frame does not send it twice. */
  const sentRef = useRef(new Map<string, StrategyActuals>());
  const retryAtRef = useRef(new Map<string, number>());

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!eventId) {
      setStrategies([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listStrategyPlans(eventId)
      .then((rows) => {
        if (!cancelled) setStrategies(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(message(err, 'Could not load the strategies'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, strategyVersion, reloads]);

  // A new race starts from a clean slate: nothing seen, sent or half-edited carries over.
  useEffect(() => {
    storeRef.current = emptyObservations();
    sentRef.current = new Map();
    retryAtRef.current = new Map();
    setDrafts({});
  }, [eventId]);

  // The countdown moves between frames, so keep the numbers moving too.
  useEffect(() => {
    if (replay || strategies.length === 0) return;
    const id = window.setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [replay, strategies.length]);

  // ── Follow ──────────────────────────────────────────────────────────────────
  const computed = useMemo(() => {
    const plans: LiveTeamPlan[] = [];
    const waiting: StrategyPlan[] = [];
    const at = now ? now() : Date.now();

    for (const strategy of strategies) {
      const teamId = matchStrategyToTeam(strategy, teams);
      const team = teamId ? teams.get(teamId) : undefined;
      const clock = raceClock(strategy.config, sessionEndsAt, at);
      if (!teamId || !team || !clock) {
        waiting.push(strategy);
        continue;
      }

      // Only the real countdown is trusted to say when something happened.
      if (clock.source === 'countdown') observeTeam(storeRef.current, teamId, team, clock);
      const observed = observedActuals(storeRef.current, teamId);
      // A replay may have been seeked past stops it never watched, so it also reads
      // the stored record of the finished race, but only the part that is already
      // in the past at the playhead. Live, everything stored is the past.
      const recorded = mergeActuals(
        replay ? actualsUpTo(strategy.actuals, stopsMade(team)) : strategy.actuals,
        observed,
      );

      const draft = drafts[strategy.id];
      const baseline = withDrafts(strategy, draft);
      plans.push({
        strategy: baseline,
        teamId,
        live: reconcile(baseline, recorded, team, clock),
        dirty: draft !== undefined && Object.keys(draft).length > 0,
      });
    }
    return { plans, waiting };
    // `tick` is a deliberate dependency: it only exists to re-run this on a timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategies, teams, sessionEndsAt, drafts, now, replay, tick]);

  const byTeam = useMemo(() => {
    const map = new Map<string, LivePlan>();
    for (const item of computed.plans) {
      if (!map.has(item.teamId)) map.set(item.teamId, item.live);
    }
    return map;
  }, [computed.plans]);

  // ── Record ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (replay) return;

    for (const item of computed.plans) {
      if (item.live.clock.source !== 'countdown') continue;
      const id = item.strategy.id;

      const known = mergeActuals(item.strategy.actuals, sentRef.current.get(id));
      const patch = diffActuals(observedActuals(storeRef.current, item.teamId), known);
      if (Object.keys(patch).length === 0) continue;
      if (Date.now() < (retryAtRef.current.get(id) ?? 0)) continue;

      // Counted as sent straight away; if the request fails it is taken back.
      sentRef.current.set(id, mergeActuals(sentRef.current.get(id), patch));
      patchStrategyActuals(id, patch)
        .then((stored) => {
          setStrategies((prev) =>
            prev.map((s) => (s.id === id ? { ...s, actuals: stored.actuals } : s)),
          );
        })
        .catch((err) => {
          sentRef.current.delete(id);
          retryAtRef.current.set(id, Date.now() + RETRY_AFTER_MS);
          setError(message(err, 'Could not record the stop against the strategy'));
        });
    }
  }, [computed.plans, replay]);

  // ── Edit ────────────────────────────────────────────────────────────────────
  const edit = useCallback(
    (strategyId: string, stintIndex: number, patch: Partial<PlannedStint>) => {
      if (replay) return;
      setDrafts((prev) => {
        const current = prev[strategyId] ?? {};
        const next = { ...current[stintIndex], ...patch };
        // Typing a length is a decision: pin it so the rebalance leaves it alone.
        if (patch.minutes !== undefined && patch.pinned === undefined) next.pinned = true;
        return { ...prev, [strategyId]: { ...current, [stintIndex]: next } };
      });
    },
    [replay],
  );

  const discard = useCallback((strategyId: string) => {
    setDrafts((prev) => {
      const { [strategyId]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  const commit = useCallback(
    async (strategyId: string) => {
      if (replay) return;
      const item = computed.plans.find((p) => p.strategy.id === strategyId);
      if (!item) return;

      setSavingId(strategyId);
      setError(null);
      try {
        const saved = await saveStrategy(strategyId, {
          title: item.strategy.title,
          config: item.strategy.config,
          roster: item.strategy.roster,
          // The reconciled stints: what happened, plus the rebalanced rest.
          stints: item.live.plan.stints,
          eventId: item.strategy.eventId,
          teamId: item.strategy.teamId,
          kartNumber: item.strategy.kartNumber,
          expectedUpdatedAt: item.strategy.updatedAt,
        });
        setStrategies((prev) => prev.map((s) => (s.id === strategyId ? saved : s)));
        discard(strategyId);
      } catch (err) {
        if (err instanceof StrategyConflictError) {
          setError(
            'Someone else saved this strategy while you were editing. ' +
              'Their version is loaded; your edits are kept so you can apply them again.',
          );
          const theirs = err.current;
          if (theirs) {
            setStrategies((prev) => prev.map((s) => (s.id === strategyId ? theirs : s)));
          } else {
            reload();
          }
        } else {
          setError(message(err, 'Could not save the strategy'));
        }
      } finally {
        setSavingId(null);
      }
    },
    [computed.plans, discard, reload, replay],
  );

  // ── Attach ──────────────────────────────────────────────────────────────────
  const attach = useCallback(
    async (strategyId: string, teamId: string) => {
      if (replay || !eventId) return;
      const team = teams.get(teamId);
      if (!team) return;

      setError(null);
      try {
        const full = await fetchStrategy(strategyId);
        // What a plan recorded in another race says nothing about this one.
        if (full.eventId !== eventId && Object.keys(full.actuals).length > 0) {
          await resetStrategyActuals(strategyId);
        }
        await saveStrategy(strategyId, {
          title: full.title,
          config: full.config,
          roster: full.roster,
          stints: full.stints,
          eventId,
          teamId,
          kartNumber: team.kartNumber || null,
          expectedUpdatedAt: full.updatedAt,
        });
        reload();
      } catch (err) {
        setError(message(err, 'Could not attach the strategy'));
      }
    },
    [eventId, replay, reload, teams],
  );

  const loadCandidates = useCallback(async () => {
    try {
      const all = await listStrategies();
      setCandidates(all.filter((s) => s.eventId !== eventId));
    } catch (err) {
      setError(message(err, 'Could not list your strategies'));
    }
  }, [eventId]);

  return {
    plans: computed.plans,
    waiting: computed.waiting,
    byTeam,
    loading,
    error,
    readOnly: replay,
    savingId,
    dismissError: () => setError(null),
    edit,
    discard,
    commit,
    attach,
    candidates,
    loadCandidates,
    reload,
  };
}
